import { useCallback, useState, useEffect } from 'react';

import qs from 'qs';

import {
  DYNAMIC_PIPELINE_INVOKE_ERROR,
  DYNAMIC_PIPELINE_PROVIDER_NOT_FOUND,
  DYNAMIC_PIPELINE_TIMEOUT_ERROR,
  PIPELINE_DEFINITIONS_PAGE_SIZE,
} from 'src/components/pipelines/constants';
import {
  Repository,
  PipelineDefinition,
} from 'src/components/pipelines/models';
import envBaseUrl from 'src/components/pipelines/utils/env-base-url';
import { authRequest } from 'src/utils/fetch';

export type PipelineError = {
  message: string;
  detail: string;
  data: Record<string, any>;
};
export type UsePipelineDefinitions = {
  data: PipelineDefinition[];
  loading: boolean;
  dynamicPipelinesError?: string;
};
type FetchPipelineDefinitionsResponse = {
  size: number;
  values: PipelineDefinition[];
  error?: PipelineError;
};

const MAX_RETRIES = 3;

export const getPipelineDefinitionsUrl = (
  repositoryFullSlug: string,
  ref: string,
  queryParams: any = {}
): string => {
  const allQueryParams = {
    page: 1,
    pagelen: PIPELINE_DEFINITIONS_PAGE_SIZE,
    revision: ref,
    ...queryParams,
  };
  return `${envBaseUrl(
    '/!api/internal',
    'pipelines',
    true
  )}/repositories/${repositoryFullSlug}/pipeline_definitions?${qs.stringify(
    allQueryParams
  )}`;
};

export const fetchPipelineDefinitions = (
  repository: Repository,
  ref: string,
  queryParams: any = {}
): Promise<FetchPipelineDefinitionsResponse> => {
  const url = getPipelineDefinitionsUrl(repository.path, ref, queryParams);
  return fetch(authRequest(url)).then(r => r.json());
};

export const areMorePagesAvailable = (
  size: number,
  currentPage: number,
  pageSize: number
): boolean => size > currentPage * pageSize;

export function usePipelineDefinitions(
  repository: Repository,
  branchReference: string,
  pageSize = PIPELINE_DEFINITIONS_PAGE_SIZE
): UsePipelineDefinitions {
  const [loading, setLoading] = useState<boolean>(false);
  const [data, setData] = useState<PipelineDefinition[]>([]);
  const [dynamicPipelinesError, setDynamicPipelinesError] = useState<
    string | undefined
  >(undefined);

  const fetchPipelinesDefinitions = useCallback(async () => {
    setLoading(true);
    setDynamicPipelinesError(undefined);
    let pipelineDefinitions: PipelineDefinition[] = [];
    let morePagesAvailable = true;
    let currentPage = 1;
    let retries = 0;
    while (morePagesAvailable && retries < MAX_RETRIES) {
      try {
        const {
          values,
          size,
          error: fetchError,
          // eslint-disable-next-line no-await-in-loop
        } = await fetchPipelineDefinitions(
          repository,
          branchReference || repository.mainbranch,
          {
            pagelen: pageSize,
            page: currentPage,
          }
        );
        if (fetchError) {
          const errorKey = fetchError.data?.key;
          if (
            errorKey === DYNAMIC_PIPELINE_INVOKE_ERROR ||
            errorKey === DYNAMIC_PIPELINE_TIMEOUT_ERROR ||
            errorKey === DYNAMIC_PIPELINE_PROVIDER_NOT_FOUND
          ) {
            setDynamicPipelinesError(fetchError.detail);
          }
          retries++;
          continue;
        }

        if (values) {
          pipelineDefinitions = [...pipelineDefinitions, ...values];
          morePagesAvailable = areMorePagesAvailable(
            size,
            currentPage,
            pageSize
          );
          currentPage++;
        } else {
          retries++;
        }
      } catch {
        morePagesAvailable = false;
      }
    }

    setLoading(false);
    setData(pipelineDefinitions);
  }, [repository, branchReference, pageSize]);

  useEffect(() => {
    fetchPipelinesDefinitions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchReference]);

  return {
    data,
    loading,
    ...(dynamicPipelinesError !== undefined ? { dynamicPipelinesError } : {}),
  };
}
